// stores/useGrandeEcole.js
import { create } from 'zustand';
import { getProfile } from '../config/activityProfileConfig2';
import api from '../config/axiosConfig';
import { UniversalLearningEngine } from '../engine/UniversalLearningEngine';
import messagerieSocket from '../socket/messagerieSocket';
import monitorSocket from '../socket/monitorSocket';

let socketInitialized = false;
let socketCleanup = null;

const useGrandeEcole = create((set, get) => ({    

  universalEngine: null,
  parcoursEngine: null,
  pesaEngine: null,

  totalLearners: 0,
  activeLearners: 0,
  totalPesasCirculating: 0,
  globalCompletionRate: 0,
  systemAlerts: [], 
  criticalAlerts: 0,

  learners: [],
  selectedLearner: null,
  currentUser: null,  
  courses: [],
  cohortes: [],
  pesaCases: [],
  staff: [],

  systemMetrics: {
    cpu: 0,
    memory: 0,
    network: 0,
    storage: 0
  },

  systemServices: [],

  systemLogs: [],
  logsEnabled: true,

  loading: false,
  error: null,
  socketConnected: false,
  socketError: null,

  isOpen: false,
  recipient: null,

  activityProfile: 'education',
  hasChosenProfile: false,
  profile: null,
  personaPath: [],
  formData: {},
  sessionId: null,
  crmContacts: [],
  crmLoading: false,
  crmError:    null,  
  crmSearch:        '',
  crmStageFilter:   'all',
  crmSortField:     'updatedAt',
  crmSortDir:       'desc',
  selectedContact: null,
  qualiData: null,
  qualiLoading: false,
  qualiError: null,
  dashboardMeta: {},
  tenantOnboardingData: null,
  onboardingCompleted: false,
  
  
  // CATÉGORIE 1 : INITIALISATION  
  initialize: async () => {
    set({ loading: true, error: null });

    try {
      // 1. Stats globales
      const statsResponse = await api.get('/admin/stats');
      const stats = statsResponse.data;

      //const timeseriesResponse = await api.get('/admin/stats/timeseries');
      //const timeseries = timeseriesResponse.data;

      // 2. Liste apprenants
      const learnersResponse = await api.get('/admin/learners');
      const learnersData = learnersResponse.data;
      
      // backend filtre + frontend vérifie
      const rawLearners = Array.isArray(learnersData)
        ? learnersData
        : (learnersData.learners || []);
      
      const learners = rawLearners
        .filter(l => l.role === 'learner' || l.isManaged === true)
        .map(l => ({
          ...l,
          // Le backend renvoie maintenant courses[] complet.
          // Si absent (vieille réponse ou endpoint legacy), on reconstruit depuis activeCourse.
          courses: Array.isArray(l.courses) && l.courses.length > 0
            ? l.courses
            : l.activeCourse
              ? [{
                  userCourseId: l.activeCourse.userCourseId,
                  parcoursId:   l.activeCourse.parcoursId   || null,
                  parcoursNom:  l.activeCourse.parcoursNom  || 'Parcours actif',
                  generator:    l.activeCourse.generator    || 'QualiopiPDF',
                  status:       l.activeCourse.status       || 'in_progress',
                  progress:     l.activeCourse.progressPercentage ?? l.activeCourse.progress ?? 0,
                  isCertified:  l.activeCourse.isCertified  ?? false,
                }]
              : [],
        }));

      // 3. Parcours
      const coursesResponse = await api.get('/admin/master-parcours');
      const parcours = coursesResponse.data;

      // 4. Cohortes
      const cohortesResponse = await api.get('/admin/cohortes');
      const cohortes = cohortesResponse.data;

      // 5. Cas PESA
      const pesaCasesResponse = await api.get('/admin/pesa-cases');
      const pesaCases = pesaCasesResponse.data;

      // 6. Staff (learner, particulier, entreprise)
      const staffResponse = await api.get('/admin/staff');
      const staff = staffResponse.data;

      get().loadActivityProfile();

      // Mise à jour avec données normalisées
      set({
        totalLearners: stats.totalLearners || learners.length,
        activeLearners: stats.activeLearners || learners.filter(l => l.status === 'active').length,
        totalPesasCirculating: stats.totalPesasCirculating || 0,
        globalCompletionRate: stats.globalCompletionRate || 0,
        criticalAlerts: stats.criticalAlerts || 0,
        learners,
        courses: parcours, 
        cohortes,
        pesaCases,
        staff,
        //timeseries,
        loading: false,
      });

      // ── 9. Charger les contacts CRM pour le profil actif ─────
      await get().fetchCrmContacts();    // ne bloque pas l’affichage principal
    } catch (error) {
      console.error('❌ Failed to initialize GrandeEcole Control:', error);
      set({ error: error.message, loading: false });
    }
  }, 
  
  fetchQualiopiData: async () => {
    set({ qualiLoading: true, qualiError: null });
    try {
      const { data } = await api.get('/admin/qualiopi');
      set({ qualiData: data, qualiLoading: false });
    } catch (err) {
      set({ qualiError: err.response?.data?.message || err.message, qualiLoading: false });
    }
  },

  fetchDashboardData: async () => {
    set({ qualiLoading: true, qualiError: null });
    try {
      const { data } = await api.get('/admin/certification/dashboard');
      set({
        qualiData: data.data,   // ← extraction des indicateurs
        qualiLoading: false,        
        dashboardMeta: {
          generator: data.generator,
          wasDowngraded: data.wasDowngraded,
          profileId: data.profileId,
        },
      });
    } catch (err) {
      set({ qualiError: err.response?.data?.message || err.message, qualiLoading: false });
    }
  },

  fetchTenantOnboarding: async () => {
    try {
      const { data } = await api.get('/admin/tenant/config');
      set({ 
        tenantOnboardingData: data.onboardingData ?? {}, 
        onboardingCompleted: data.onboardingCompleted ?? false,
      });
    } catch (err) {
      console.warn('Erreur chargement onboarding', err.message);
    }
  },

  generateReleve2: async (userCourseId) => {
    try {
      const response = await api.get(`/admin/qualiopi/releve/${userCourseId}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `releve_${userCourseId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Libérer la mémoire
      window.URL.revokeObjectURL(url);

    } catch (err) {
      // Si le serveur renvoie une erreur JSON dans un blob, la décoder
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          const json = JSON.parse(text);
          console.error('Erreur génération PDF:', json.message || json.error);
        } catch {
          console.error('Erreur génération PDF:', text);
        }
      } else {
        console.error('Erreur génération PDF:', err.message);
      }
    }
  },

  generateReleve: async (userCourseId, generator) => {
    if (!userCourseId) {
      console.warn('[generateReleve] userCourseId manquant');
      return;
    }
  
    try {
      // On passe le generator en query param pour que le backend puisse
      // l'utiliser comme forceGenerator dans generateCertification().
      const params = generator ? `?generator=${encodeURIComponent(generator)}` : '';
      const response = await api.get(
        `/admin/qualiopi/releve/${userCourseId}${params}`,
        { responseType: 'blob' }
      );
  
      // Déduction du nom de fichier depuis les headers ou fallback
      const disposition = response.headers['content-disposition'] || '';
      const match       = disposition.match(/filename="?([^";\n]+)"?/);
      const filename    = match?.[1] || `releve_${generator || 'qualiopi'}_${userCourseId}.pdf`;
  
      const url  = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href  = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
  
    } catch (error) {
      console.error('[generateReleve] Erreur :', error);
      throw error; // remonté au CertificationDashboard pour affichage
    }
  },

  setActivityProfile: async (profileId) => {
    const profile = getProfile(profileId);
    set({
      activityProfile: profileId,
      profile,
      layout: profile.layout || getProfile('education').layout,
      hasChosenProfile: true,
    });
    try { localStorage.setItem('gc_activity_profile', profileId); } catch {}
    try { localStorage.setItem('gc_has_chosen_profile', 'true'); } catch {}
    
    get().fetchCrmContacts();

    // Synchronisation avec le serveur (non bloquante)
    try {
      await api.put('/admin/tenant/profile', { profileId });
    } catch (err) {
      console.warn('Impossible de synchroniser le profil avec le serveur :', err.message);
      // pas bloquant, le localStorage prime
    }
  },

  loadActivityProfile: () => {
    try {
      const saved = localStorage.getItem('gc_activity_profile') ?? 'education';
      const hasChosen = localStorage.getItem('gc_has_chosen_profile') === 'true';
      const profile = getProfile(saved);
      set({ 
        activityProfile: saved, 
        profile,
        hasChosenProfile: hasChosen
      });
      get().fetchTenantOnboarding();

    } catch {
      set({ 
        activityProfile: 'education', 
        profile: getProfile('education'),
        hasChosenProfile: false
      });
    }
  },

  updateTenantOnboarding: async (tenantId, payload) => {
  const { data } = await api.put(`/admin/tenant/onboarding`, payload);
  set({
    tenantOnboardingData: data.onboardingData,
    // ↓ crucial : permet à GrandeEcoleV2 de détecter isComplete=true
    onboardingCompleted: data.onboardingCompleted ?? true,
  });
},
  
  fetchCrmContacts: async () => {
    set({ crmLoading: true, crmError: null });
    try {
      const res = await api.get('/admin/crm/contacts');   // Axios gère le token
      const data = res.data;
      const contacts = Array.isArray(data) ? data : (data.contacts ?? []);
      set({ crmContacts: contacts, crmLoading: false });
    } catch (err) {
      // Fallback : pas de données démo pour l’instant → tableau vide
      console.warn('CRM API indisponible – contacts vides', err);
      set({ crmContacts: [], crmLoading: false, crmError: err.response?.data?.error || err.message });
    }
  },

// ── CRM – create ──────────────────────────────────────────
  createCrmContact: async (contactData) => {
    try {
      const res = await api.post('/admin/crm/contacts', contactData);
      const newContact = res.data.contact || res.data;
      set(s => ({ crmContacts: [newContact, ...s.crmContacts] }));
      return { success: true, contact: newContact };
    } catch (err) {
      // Fallback optimiste (mode démo) si l’API est absente
      const stage0 = get().profile?.crmConfig?.pipelineStages?.[0]?.id ?? 'prospect';
      const optimistic = {
        _id: `demo_${Date.now()}`,
        ...contactData,
        stage: contactData.stage || stage0,
        score: contactData.score ?? 50,
        tags: contactData.tags ?? [],
        notes: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      set(s => ({ crmContacts: [optimistic, ...s.crmContacts] }));
      return { success: true, contact: optimistic };
    }
  },

// ── CRM – update ──────────────────────────────────────────
  updateCrmContact: async (id, patch) => {
    // Optimistic update
    set(s => ({
      crmContacts: s.crmContacts.map(c =>
        (c._id === id || c.id === id)
          ? { ...c, ...patch, updatedAt: new Date().toISOString() }
          : c
      ),
    }));
    try {
      await api.patch(`/admin/crm/contacts/${id}`, patch);
    } catch (err) {
      console.warn('CRM update API error – optimistic already applied', err);
    }
  },

// ── CRM – delete ──────────────────────────────────────────
  deleteCrmContact: async (id) => {
    set(s => ({ crmContacts: s.crmContacts.filter(c => c._id !== id && c.id !== id) }));
    try {
      await api.delete(`/admin/crm/contacts/${id}`);
    } catch (err) {
      console.warn('CRM delete API error', err);
    }
  },

// ── CRM – move stage ──────────────────────────────────────
  moveCrmContactStage: (id, newStage) => {
    get().updateCrmContact(id, { stage: newStage });
  },

// ── CRM – add note ────────────────────────────────────────
  addCrmNote: async (contactId, noteText) => {
    const note = {
      id: `note_${Date.now()}`,
      text: noteText,
      author: get().user?.nom ?? get().currentUser?.nom ?? 'Admin',
      createdAt: new Date().toISOString(),
    };
    // Optimistic
    set(s => ({
      crmContacts: s.crmContacts.map(c =>
        (c._id === contactId || c.id === contactId)
          ? { ...c, notes: [note, ...(c.notes ?? [])] }
          : c
      ),
    }));
    try {
      await api.post(`/admin/crm/contacts/${contactId}/notes`, { text: noteText });
    } catch (err) {
      console.warn('CRM add note API error', err);
    }
  },

  filteredCrmContacts: () => {
    const { crmContacts, crmSearch, crmStageFilter, crmSortField, crmSortDir } = get();
    let list = [...crmContacts];
 
    if (crmSearch.trim()) {
      const q = crmSearch.toLowerCase();
      list = list.filter(c =>
        [c.nom, c.prenom, c.email, c.entreprise, c.telephone]
          .filter(Boolean)
          .some(f => f.toLowerCase().includes(q))
      );
    }
 
    if (crmStageFilter !== 'all') {
      list = list.filter(c => c.stage === crmStageFilter);
    }
 
    list.sort((a, b) => {
      let av = a[crmSortField] ?? '';
      let bv = b[crmSortField] ?? '';
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      return crmSortDir === 'asc'
        ? av < bv ? -1 : av > bv ? 1 : 0
        : av > bv ? -1 : av < bv ? 1 : 0;
    });
 
    return list;
  },
  
  // CATÉGORIE 2 : GESTION LEARNERS  
  loadLearner: async (userId) => {
    set({ loading: true, error: null });

    try {
      const response = await api.get(`/admin/learners/${userId}/full`);
      const learnerData = response.data;

      if (!learnerData.userId) {
        throw new Error(`Invalid learner data: missing userId`);
      }

      const universalEngine = new UniversalLearningEngine(learnerData);

      if (typeof window !== 'undefined') {
        window.__UNIVERSAL_LEARNING_STORE__ = {
          setState: (updates) => {
            set((state) => {
              const oldPesas = state.selectedLearner?.totalPesas || 0;
              const newPesas = updates.totalPesas || 0;
              const delta = newPesas - oldPesas;

              return {
                selectedLearner: state.selectedLearner ? {
                  ...state.selectedLearner,
                  ...updates
                } : null,
                totalPesasCirculating: Math.max(0, state.totalPesasCirculating + delta)
              };
            });
          }
        };
      }

      if (learnerData.activeCourseId) {
        try {
          const courseResponse = await api.get(`/admin/learners/${userId}/active-parcours`);
          const { course, progress } = courseResponse.data;
          await universalEngine.initParcoursEngine(course, progress);
        } catch (courseError) {
          console.warn('No active course or failed to load:', courseError.message);
        }
      }

      if (learnerData.activePesaCaseId) {
        try {
          const caseResponse = await api.get(`/admin/pesa-cases/${learnerData.activePesaCaseId}`);
          const caseData = caseResponse.data;
          const savedState = learnerData.engineStates?.pesa || null;
          await universalEngine.initPesaEngine(caseData, savedState);
        } catch (pesaError) {
          console.warn('Failed to load PESA case:', pesaError.message);
        }
      }

      set({
        universalEngine,
        parcoursEngine: universalEngine.getParcoursEngine(),
        pesaEngine: universalEngine.getPesaEngine(),
        selectedLearner: {
          ...learnerData,
          stats: universalEngine.getGlobalStats(),
          portfolio: universalEngine.getPortfolio()
        },
        loading: false
      });

      console.log('Learner loaded:', userId);
    } catch (error) {
      console.error('❌ Failed to load learner:', error);
      set({ 
        error: error.message, 
        loading: false,
        selectedLearner: null,
        universalEngine: null,
        parcoursEngine: null,
        pesaEngine: null
      });
    }
  },

  createLearner: async (learnerData) => {
    set({ loading: true, error: null });

    try {
      const response = await api.post('/admin/learners', learnerData);
      const newLearner = response.data.learner || response.data;

      // Mise à jour optimiste du store
      set(state => ({
        learners: [...state.learners, newLearner],
        totalLearners: (state.totalLearners || 0) + 1,
        loading: false
      }));

      console.log('Apprenant créé:', newLearner._id || newLearner.userId || newLearner.id);

      return {
        success: true,
        learner: newLearner,
        message: response.data.message || 'Apprenant créé avec succès',
        credentialsSent: response.data.credentialsSent || false
      };

    } catch (err) {
      // Extraction intelligente du message d'erreur serveur
      const serverError = err.response?.data?.error || err.response?.data?.message;
      const errMsg = serverError || err.message || 'Erreur lors de la création de l\'apprenant';

      console.error('❌ Erreur création apprenant:', err);

      set({
        loading: false,
        error: errMsg
      });

      // Cas spécifiques pour meilleure gestion frontend
      if (errMsg.includes('obligatoires')) {
        return {
          success: false,
          error: errMsg,
          type: 'validation',
          missingFields: errMsg.match(/(\w+)(?= obligatoire)/g) || []
        };
      }

      if (errMsg.includes('tenantId') || errMsg.includes('organisation')) {
        return {
          success: false,
          error: errMsg,
          type: 'tenant_error',
          suggestion: 'Veuillez vérifier votre compte ou contacter le support'
        };
      }

      return {
        success: false,
        error: errMsg,
        type: 'server_error'
      };
    }
  },

  updateLearner: async (learnerId, updates) => {
    set({ loading: true });
    try {
      const response = await api.put(`/admin/learners/${learnerId}`, updates);
      const updatedLearner = response.data.learner || response.data;

      set(state => ({
        learners: state.learners.map(l =>
          (l._id === learnerId || l.userId === learnerId) ? { ...l, ...updatedLearner } : l
        ),
        selectedLearner: state.selectedLearner?.userId === learnerId 
          ? { ...state.selectedLearner, ...updatedLearner }
          : state.selectedLearner,
        loading: false
      }));

      return { success: true, learner: updatedLearner };
    } catch (err) {
      console.error('❌ Erreur update apprenant:', err);
      set({ loading: false });
      return { success: false, error: err.message };
    }
  },

  deleteLearner: async (learnerId) => {
    set({ loading: true });
    try {
      await api.delete(`/admin/learners/${learnerId}`);

      set(state => ({
        learners: state.learners.filter(l => (l._id || l.userId) !== learnerId),
        totalLearners: Math.max(0, state.totalLearners - 1),
        selectedLearner: state.selectedLearner?.userId === learnerId 
          ? null 
          : state.selectedLearner,
        loading: false
      }));

      console.log('Apprenant supprimé:', learnerId);
      return { success: true };
    } catch (err) {
      console.error('❌ Erreur suppression apprenant:', err);
      set({ loading: false });
      return { success: false, error: err.message };
    }
  },

  saveLearnerProgress: async () => {
    const { universalEngine, selectedLearner } = get();
    
    if (!universalEngine || !selectedLearner) {
      console.warn('No engine or learner to save');
      return { success: false, error: 'No active learner' };
    }

    try {
      const payload = universalEngine.serialize();
      
      const response = await api.put(
        `/admin/learners/${selectedLearner.userId}/progress`,
        payload
      );

      if (response.data.success) {
        set({
          selectedLearner: {
            ...selectedLearner,
            lastSync: response.data.timestamp
          }
        });

        console.log('Progress saved for:', selectedLearner.userId);
        return { success: true };
      } else {
        throw new Error(response.data.error || 'Save failed');
      }
    } catch (error) {
      console.error('❌ Failed to save progress:', error);
      return { success: false, error: error.message };
    }
  },
  
  unloadLearner: () => {
    const { universalEngine } = get();
    if (universalEngine) {
      universalEngine.destroy();
    }
    set({
      universalEngine: null,
      parcoursEngine: null,
      pesaEngine: null,
      selectedLearner: null
    });
  },
  
  
  // CATÉGORIE 3 : GESTION COHORTES
  createCohorte: async (cohorteData) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post('/admin/cohortes', cohorteData);
      const newCohorte = response.data.cohorte || response.data;

      set(state => ({
        cohortes: [...state.cohortes, newCohorte],
        loading: false
      }));

      console.log('Cohorte créée:', newCohorte._id);
      return { success: true, cohorte: newCohorte };
    } catch (error) {
      const errMsg = error.response?.data?.error || error.message || 'Erreur création cohorte';
      console.error('❌ Failed to create cohorte:', error);
      set({ loading: false, error: errMsg });
      return { success: false, error: errMsg };
    }
  },

  updateCohorte: async (cohorteId, updates) => {
    set({ loading: true, error: null });
    try {
      const response = await api.put(`/admin/cohortes/${cohorteId}`, updates);
      const updated = response.data.cohorte || response.data;

      set(state => ({
        cohortes: state.cohortes.map(c =>
          (c._id || c.id) === cohorteId ? { ...c, ...updated } : c
        ),
        loading: false
      }));

      console.log('Cohorte mise à jour:', cohorteId);
      return { success: true, cohorte: updated };
    } catch (error) {
      const errMsg = error.response?.data?.error || error.message || 'Erreur mise à jour cohorte';
      console.error('❌ Failed to update cohorte:', error);
      set({ loading: false, error: errMsg });
      return { success: false, error: errMsg };
    }
  },

  deleteCohorte: async (cohorteId, options = {}) => {
    const { forceDeleteLearners = false, confirmMessage } = options;

    set({ loading: true, error: null });

    try {
      // Si tu veux passer l'option forceDeleteLearners au serveur
      const response = await api.delete(`/admin/cohortes/${cohorteId}`, {
        data: { forceDeleteLearners }  // ← Axios permet d'envoyer un body avec DELETE
      });

      // Mise à jour optimiste du store
      set(state => ({
        cohortes: state.cohortes.filter(c => (c._id || c.id) !== cohorteId),
        loading: false
      }));

      console.log('Cohorte supprimée:', cohorteId);
      return {
        success: true,
        message: response.data.message || 'Cohorte supprimée avec succès',
        learnersHandled: response.data.learnersHandled || 'détachés'
      };
    } catch (error) {
      const errData = error.response?.data;
      const errMsg = errData?.error || error.message || 'Erreur suppression cohorte';

      console.error('❌ Failed to delete cohorte:', error);

      set({ loading: false, error: errMsg });

      // Cas spécifiques pour meilleure UX
      if (errMsg.includes('active contenant des apprenants')) {
        return {
          success: false,
          error: errMsg,
          needsConfirmation: true,
          suggestion: 'La cohorte contient des apprenants. Confirmer la suppression ? (forceDeleteLearners)'
        };
      }

      return { success: false, error: errMsg };
    }
  },
  
  
  // CATÉGORIE 4 : GESTION STAFF
  createStaff: async (staffData) => {
    set({ loading: true, error: null });

      try {
        // Validation alignée sur les champs réels du formulaire
        if (!staffData.nom || !staffData.email || !staffData.pesaRole) {
          throw new Error('Nom, email et poste obligatoires');
        }

        const response = await api.post('/admin/staff', {
          firstName:   staffData.prenom,
          lastName:    staffData.nom,
          email:       staffData.email,
          pesaRole:    staffData.pesaRole,
          status:      staffData.status || 'active',
          phoneNumber: staffData.phoneNumber || '',
          photoUrl:    staffData.photoUrl || '',
          notes:       staffData.notes || '',
        });

        const newStaff = response.data.staff || response.data;

        set(state => ({
          staff: [...state.staff, newStaff],
          totalStaff: (state.totalStaff || 0) + 1,
          loading: false
        }));

        console.log('Membre du personnel créé:', newStaff.staffId || newStaff._id);

        return {
          success: true,
          staff: newStaff,
          tempPassword: response.data.tempPassword || null,
          message: response.data.message || 'Membre créé avec succès',
        credentialsSent: response.data.credentialsSent || false
      };

    } catch (err) {
      const serverError = err.response?.data?.error || err.response?.data?.message;
      const errMsg = serverError || err.message || 'Erreur lors de la création du membre';

      console.error('❌ Erreur création staff:', err);
      set({ loading: false, error: errMsg });

      return { success: false, error: errMsg };
    }
  },

  updateStaff: async (staffId, updates) => {
    set({ loading: true });

      try {
        const response = await api.put(`/admin/staff/${staffId}`, {
          firstName:   updates.prenom || updates.nom,
          lastName:    updates.nom,
          email:       updates.email,
          pesaRole:    updates.pesaRole,
          status:      updates.status,
          phoneNumber: updates.phoneNumber,
          photoUrl:    updates.photoUrl,
          notes:       updates.notes,
          cohorte:     updates.cohorte,
        });

        const updatedStaff = response.data.staff || response.data;

        set(state => ({
          staff: state.staff.map(s =>
            (s._id === staffId || s.id === staffId || s.staffId === staffId)
              ? { ...s, ...updatedStaff }
              : s
          ),
          loading: false
        }));

      return { success: true, staff: updatedStaff };

    } catch (err) {
      const serverError = err.response?.data?.error || err.response?.data?.message;
      const errMsg = serverError || err.message;

      console.error('❌ Erreur update staff:', err);
      set({ loading: false });

      return { success: false, error: errMsg };
    }
  },

  deleteStaff: async (staffId) => {
    set({ loading: true });
  
    try {
      const { staff } = get();
      const member = staff.find(s => 
        (s._id || s.id || s.userId) === staffId
      );
  
      if (!member) {
        throw new Error('Membre du personnel introuvable');
      }
  
      await api.delete(`/admin/staff/${staffId}`);
  
      set(state => ({
        staff: state.staff.filter(s => 
          (s._id || s.id || s.userId) !== staffId
        ),
        loading: false
      }));
  
      console.log('Membre du personnel supprimé:', staffId);
      return { success: true };
  
    } catch (err) {
      console.error('❌ Erreur suppression staff:', err);
      set({ loading: false });
      return { success: false, error: err.message };
    }
  },

  refreshStaff: async () => {
    set({ loading: true });
  
    try {
      const response = await api.get('/admin/staff');
      const staff = response.data;
  
      set({ 
        staff: Array.isArray(staff) ? staff : staff.staff || [],
        loading: false 
      });
  
      console.log('Liste du personnel rafraîchie');
    } catch (err) {
      console.error('❌ Erreur refresh staff:', err);
      set({ loading: false });
    }
  },
  
  
  // CATÉGORIE 5 : MONITORING & SYSTÈME
  startMonitoring: () => {
    if (socketInitialized) return;
    socketInitialized = true;

    monitorSocket.connect();

    const onConnect    = () => set({ socketConnected: true, socketError: null });

    const onConnectError = (err) => set({ socketError: err.message });

    const onDisconnect = () => set({ socketConnected: false });

    const onMetrics = (metrics) => {
      set({ systemMetrics: metrics });
    };

    const onServices = (services) => {
      set({ systemServices: services });
    };

    const onLog = (logEntry) => {
      set((state) => ({
        systemLogs: [
          { 
            ...logEntry, 
            timestamp: logEntry.timestamp || new Date().toISOString() 
          },
          ...state.systemLogs.slice(0, 99)
        ]
      }));
    };

    const onAlert = (alert) => {
      set(state => {
        const isCritical = ['critical', 'emergency'].includes(alert.severity);
        
        return {
          systemAlerts: [alert, ...state.systemAlerts].slice(0, 100),
          criticalAlerts: isCritical 
            ? state.criticalAlerts + 1 
            : state.criticalAlerts
        };
      });
    };

    monitorSocket.on('connect', onConnect);
    monitorSocket.on('connect_error', onConnectError);
    monitorSocket.on('disconnect', onDisconnect);
    monitorSocket.on('system:metrics', onMetrics);
    monitorSocket.on('system:services', onServices);
    monitorSocket.on('system:log', onLog);
    monitorSocket.on('system:alert', onAlert);

    socketCleanup = () => {
      console.log('🧹 Cleaning up socket listeners');
      monitorSocket.off('connect', onConnect);
      monitorSocket.off('connect_error', onConnectError);
      monitorSocket.off('disconnect', onDisconnect);
      monitorSocket.off('system:metrics', onMetrics);
      monitorSocket.off('system:services', onServices);
      monitorSocket.off('system:log', onLog);
      monitorSocket.off('system:alert', onAlert);
      socketInitialized = false;
    };

    if (!monitorSocket.connected) {
      monitorSocket.connect();
    }

    const heartbeat = setInterval(() => {
      if (monitorSocket.connected) {
        monitorSocket.emit('admin:ping');
      }
    }, 30000);

    const originalCleanup = socketCleanup;
    socketCleanup = () => {
      clearInterval(heartbeat);
      originalCleanup();
    };
  },

  stopMonitoring: () => {
    if (socketCleanup) {
      socketCleanup();
    }
    if (monitorSocket.connected) {
      monitorSocket.disconnect();
    }
    set({ socketConnected: false });
  },

  flushLearnerCache: async () => {
    try {
      await api.post('/admin/cache/flush-learners');
      const response = await api.get('/admin/learners');
      set({ 
        learners: Array.isArray(response.data) 
          ? response.data.filter(l => l.role === 'learner' || l.isManaged === true)
          : (response.data.learners || []).filter(l => l.role === 'learner' || l.isManaged === true)
      });
      console.log('Cache learners vidé et rechargé');
    } catch (err) {
      console.error('❌ Erreur flush cache:', err);
    }
  },

  runSystemHealthCheck: async () => {
    try {
      const response = await api.get('/admin/health/full');
      return response.data;
    } catch (err) {
      console.error('❌ Erreur health check:', err);
      throw err;
    }
  },

  restartServices: async () => {
    set({ loading: true });
    try {
      await api.post('/admin/services/restart');
      console.log('Services redémarrés');
      set({ loading: false });
    } catch (err) {
      console.error('❌ Erreur restart:', err);
      set({ loading: false });
      throw err;
    }
  },

  restartSingleService: async (serviceId) => {
    try {
      const response = await api.post(`/admin/system/services/${serviceId}/restart`);
      if (response.data.success) {
        set(state => ({
          systemServices: state.systemServices.map(s => 
            s.id === serviceId ? { ...s, status: 'online', uptime: '0s', load: 0 } : s
          )
        }));
        console.log(`Service ${serviceId} redémarré`);
        return true;
      }
    } catch (err) {
      console.error(`❌ Erreur restart service ${serviceId}:`, err);
      throw err;
    }
  },

  runBackup: async () => {
    set({ loading: true });
    try {
      await api.post('/admin/backup/now');
      console.log('Backup lancé');
      set({ loading: false });
    } catch (err) {
      console.error('❌ Erreur backup:', err);
      set({ loading: false });
      throw err;
    }
  },

  toggleLogs: () => {
    set((state) => ({ logsEnabled: !state.logsEnabled }));
  },

  clearLogs: () => {
    set({ systemLogs: [] });
  },  
  
  // CATÉGORIE 6 : ALERTES & MODÉRATION
  resolveAlert: async (alertId, notes = '') => {
    try {
      const response = await api.post(`/admin/alerts/${alertId}/resolve`, {
        notes
      });

      if (response.data.success) {
        // Retirer l'alerte de la liste locale
        set(state => ({
          systemAlerts: state.systemAlerts.filter(a => a.id !== alertId),
          criticalAlerts: state.systemAlerts.find(a => a.id === alertId && ['critical', 'emergency'].includes(a.severity))
            ? Math.max(0, state.criticalAlerts - 1)
            : state.criticalAlerts
        }));

        console.log(`Alerte ${alertId} résolue`);
        return { success: true };
      }
    } catch (err) {
      console.error(`❌ Erreur résolution alerte ${alertId}:`, err);
      return { success: false, error: err.message };
    }
  },

  ignoreAlert: async (alertId, reason = '') => {
    try {
      const response = await api.post(`/admin/alerts/${alertId}/ignore`, {
        reason
      });

      if (response.data.success) {
        // Retirer l'alerte de la liste locale
        set(state => ({
          systemAlerts: state.systemAlerts.filter(a => a.id !== alertId),
          criticalAlerts: state.systemAlerts.find(a => a.id === alertId && ['critical', 'emergency'].includes(a.severity))
            ? Math.max(0, state.criticalAlerts - 1)
            : state.criticalAlerts
        }));

        console.log(`Alerte ${alertId} ignorée`);
        return { success: true };
      }
    } catch (err) {
      console.error(`❌ Erreur ignorer alerte ${alertId}:`, err);
      return { success: false, error: err.message };
    }
  },

  resetCriticalAlerts: () => {
    set({ criticalAlerts: 0 });
  },

  
  
  // CATÉGORIE 7 : STATS & UTILITAIRES
  recalculateGlobalStats: () => {
    const { learners } = get();

    const totalPesas = learners.reduce((sum, l) => sum + (l.totalPesas || l.pesas || 0), 0);
    const avgProgress = learners.length > 0
      ? learners.reduce((sum, l) => sum + (l.globalProgress || 0), 0) / learners.length
      : 0;

    const activeLearners = learners.filter(l => l.status === 'active').length;
    
    const criticalAlerts = learners.filter(l => 
      l.globalProgress < 30 && l.status === 'active'
    ).length;

    set({
      totalPesasCirculating: totalPesas,
      globalCompletionRate: Math.round(avgProgress),
      activeLearners,
      criticalAlerts
    });
  },

  // Méthodes Engine (parcours, pesa)
  validateGranule: (granuleId, results) => {
    const { universalEngine, selectedLearner } = get();
    if (!universalEngine || !selectedLearner) return;

    universalEngine.onParcoursGranuleValidated(granuleId, results);

    set({
      selectedLearner: {
        ...selectedLearner,
        stats: universalEngine.getGlobalStats(),
        portfolio: universalEngine.getPortfolio()
      }
    });
  },

  applyPesaAction: (actionId) => {
    const { universalEngine, pesaEngine, selectedLearner } = get();
    if (!universalEngine || !pesaEngine || !selectedLearner) return;

    const result = pesaEngine.applyAction(actionId);
    universalEngine.onPesaActionApplied(actionId, result);

    set({
      selectedLearner: {
        ...selectedLearner,
        stats: universalEngine.getGlobalStats()
      }
    });

    return result;
  },

  openCompose: (recipientData) => set({ 
    isOpen: true, 
    recipient: recipientData
  }),

  closeMessaging: () => set({ isOpen: false, recipient: null }),
  setCurrentUser: (user) => set({ currentUser: user }),

  

  submitMessage: async (subject, body, priority = 'normal') => {
    const { recipient } = get();

    if (!recipient || !recipient.id) {
      return { success: false, error: 'Destinataire manquant' };
    }

    try {
      // On construit l'objet EXACTEMENT comme le MessagerieManager
      const payload = {
        to: [recipient.id],           // Tableau d'IDs
        cc: [],                       // Obligatoire si ton schéma le demande
        bcc: [],                      // Obligatoire si ton schéma le demande
        subject: subject,
        body: body,
        messageType: 'conversation',
        metadata: {
          priority: priority,
          requireReadReceipt: false,  // Valeur par défaut
          encrypted: false,           // Valeur par défaut
          scheduledAt: null,          // Valeur par défaut
          sentFrom: 'QuickCompose'
        },
        attachments: []               // Obligatoire pour éviter "null" côté serveur
      };

      console.log("📤 Envoi payload complet:", payload);
      
      messagerieSocket.sendMessage(payload);

      setTimeout(() => {
        messagerieSocket.getSent({ page: 1, limit: 20 });
      }, 300);

      set({ isOpen: false, recipient: null });
      return { success: true };

    } catch (err) {
      console.error('❌ Erreur envoi socket:', err);
      return { success: false, error: err.message };
    }
  },

  setCrmSearch:       (val)   => set({ crmSearch: val }),
  setCrmStageFilter:  (stage) => set({ crmStageFilter: stage }),
  setCrmSort:         (field, dir) => set({ crmSortField: field, crmSortDir: dir }),
  setSelectedContact: (contact) => set({ selectedContact: contact }),
  clearSelectedContact: ()    => set({ selectedContact: null }),

  
}));

export default useGrandeEcole;